<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<!-- Summary -->
				<app-tab-item :tab="tab" value="summary">
					<app-heading :title="resort.name" />
					<app-section>
						<app-row>
							<app-cell>
								<div class="font-weight-bold">Test</div>
							</app-cell>
						</app-row>
					</app-section>
				</app-tab-item>

				<!-- Areas -->
				<app-tab-item :tab="tab" value="areas">
					<app-heading :title="`${resort.name} Areas`" />
					<app-row>
						<app-cell>
							<app-data-table
								:items="areas.items"
								:total="areas.count"
								:headers="areas.headers"
								@update="fetchAreas"
							>
								<template v-slot:content="{ item }">
									<app-json :data="item.content || {}" />
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>

				<!-- Channels -->
				<app-tab-item :tab="tab" value="channels">
					<app-heading :title="`${resort.name} Channels`" />
					<app-row>
						<app-cell>
							<app-data-table
								:items="channels.items"
								:total="channels.count"
								:headers="channels.headers"
								@update="fetchChannels"
							>
								<template v-slot:content="{ item }">
									<app-json :data="item.content || {}" />
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										color="secondary"
										icon="fa-sync"
										@click="syncChannel(item.studeo_id)"
										v-if="$ravello.can('system.post')"
										table
									>
										Sync
									</app-button>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>

				<!-- Complexes -->
				<app-tab-item :tab="tab" value="complexes">
					<app-heading :title="`${resort.name} Complexes`" />
					<app-row>
						<app-cell>
							<app-data-table
								:items="complexes.items"
								:total="complexes.count"
								:headers="complexes.headers"
								@update="fetchComplexes"
							>
								<template v-slot:address="{ item }">
									<app-json :data="item.address || {}" />
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										color="secondary"
										:to="{
											name: 'studeo-resort-complex',
											params: { resort: id, complex: item._id }
										}"
										icon="fa-eye"
										table
									>
										View
									</app-button>
									<app-button
										color="secondary"
										icon="fa-sync"
										@click="syncComplex(item.studeo_id)"
										v-if="$ravello.can('system.post')"
										table
									>
										Sync
									</app-button>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>

				<!-- Locations -->
				<app-tab-item :tab="tab" value="locations">
					<app-heading :title="`${resort.name} Locations`" />
					<app-row>
						<app-cell>
							<app-data-table
								:items="locations.items"
								:total="locations.count"
								:headers="locations.headers"
								@update="fetchLocations"
							>
								<template v-slot:content="{ item }">
									<app-json :data="item.content || {}" />
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>

				<!-- Units -->
				<app-tab-item :tab="tab" value="units">
					<app-heading :title="`${resort.name} Units`" />
					<app-filter @click="applyFilters('units')">
						<app-row no-padding>
							<app-cell cols="6" sm="3">
								<input-text
									v-model="filters.units.code"
									label="Unit code"
									clearable
									dense
								></input-text>
							</app-cell>
						</app-row>
					</app-filter>
					<app-row>
						<app-cell>
							<app-data-table
								:items="units.items"
								:total="units.count"
								:headers="units.headers"
								@update="fetchUnits"
							>
								<template v-slot:address="{ item }">
									<app-json :data="item.address || {}" />
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										color="secondary"
										:to="{
											name: 'studeo-resort-unit',
											params: { resort: id, unit: item._id }
										}"
										icon="fa-eye"
										table
									>
										View
									</app-button>
									<app-button
										color="secondary"
										icon="fa-sync"
										@click="syncUnit(item.code)"
										v-if="$ravello.can('system.post')"
										table
									>
										Sync
									</app-button>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>

				<!-- Travel agents -->
				<app-tab-item :tab="tab" value="travelagents">
					<app-heading :title="`${resort.name} Travel Agents`" />
					<app-row>
						<app-cell>
							<app-data-table
								:items="travelAgents.items"
								:total="travelAgents.count"
								:headers="travelAgents.headers"
								@update="fetchTravelAgents"
							/>
						</app-cell>
					</app-row>
				</app-tab-item>

				<!-- Reservations -->
				<app-tab-item :tab="tab" value="reservations">
					<app-heading :title="`${resort.name} Reservations`">
						<template v-slot:right>
							<input-file
								v-if="$ravello.can('studeo_admin.post')"
								type="csv"
								button
								small
								outlined
								label="Import From Studeo"
								@load="onImport"
							/>
						</template>
					</app-heading>
					<app-filter @click="applyFilters('reservations')">
						<app-row no-padding>
							<app-cell cols="6" sm="3">
								<input-date-picker
									v-model="filters.reservations.checkin"
									label="Checkin date"
									clearable
									dense
								></input-date-picker>
							</app-cell>
							<app-cell cols="6" sm="3">
								<input-date-picker
									v-model="filters.reservations.checkout"
									label="Checkout date"
									clearable
									dense
								></input-date-picker>
							</app-cell>
							<app-cell cols="6" sm="3">
								<input-text
									v-model="filters.reservations.unit_code"
									label="Unit code"
									clearable
									dense
								></input-text>
							</app-cell>
							<app-cell cols="6" sm="3">
								<input-text
									v-model="filters.reservations.studeo_id"
									label="Confirmation ID"
									clearable
									dense
								></input-text>
							</app-cell>
						</app-row>
					</app-filter>
					<app-row>
						<app-cell>
							<app-data-table
								:items="reservations.items"
								:total="reservations.count"
								:headers="reservations.headers"
								@update="fetchReservations"
							>
								<template v-slot:status="{ item }">
									<span>{{ item.status.reservation }}</span>
									<span>&nbsp;({{ item.status.payment }})</span>
								</template>
								<template v-slot:json="{ item }">
									<app-json :data="item" />
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										color="secondary"
										icon="fa-sync"
										@click="syncReservation(item.studeo_id)"
										v-if="$ravello.can('system.post')"
										table
									>
										Sync
									</app-button>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>
			</template>
		</app-tabs>
	</app-page>
</template>

<script>
import { omitBy, isNil } from 'lodash'

import Tab from '@/types/Tab'
import DataTable from '@/types/DataTable'

export default {
	async created() {
		try {
			this.resort = await this.$ravello.request()
				.v(2)
				.to('resorts')
				.to(this.id)
				.get()
		} catch (err) {
			this.$notifications.pushAlert('error', err.data.message)
		}

		this.tabs = [new Tab('summary', {
			name: 'Summary',
			to: { name: 'studeo-resort', params: { resort: this.id } },
			value: `/studeo-resorts/${this.id}`,
			exact: true,
		}), new Tab('reservations', {
			name: 'Reservations',
			to: { name: 'studeo-resort-child', params: { resort: this.id, tab: 'reservations' } },
			value: `/studeo-resorts/${this.id}/reservations`,
		}), new Tab('units', {
			name: 'Units',
			to: { name: 'studeo-resort-child', params: { resort: this.id, tab: 'units' } },
			value: `/studeo-resorts/${this.id}/units`,
		}), new Tab('channels', {
			name: 'Channels',
			to: { name: 'studeo-resort-child', params: { resort: this.id, tab: 'channels' } },
			value: `/studeo-resorts/${this.id}/channels`,
		}), new Tab('complexes', {
			name: 'Complexes',
			to: { name: 'studeo-resort-child', params: { resort: this.id, tab: 'complexes' } },
			value: `/studeo-resorts/${this.id}/complexes`,
		}), new Tab('areas', {
			name: 'Areas',
			to: { name: 'studeo-resort-child', params: { resort: this.id, tab: 'areas' } },
			value: `/studeo-resorts/${this.id}/areas`,
		}), new Tab('locations', {
			name: 'Locations',
			to: { name: 'studeo-resort-child', params: { resort: this.id, tab: 'locations' } },
			value: `/studeo-resorts/${this.id}/locations`,
		}), new Tab('travelagents', {
			name: 'Travel Agents',
			to: { name: 'studeo-resort-child', params: { resort: this.id, tab: 'travelagents' } },
			value: `/studeo-resorts/${this.id}/travelagents`,
		})]
	},
	data() {
		return {
			resort: {
				name: '',
			},
			tabs: [],
			channels: new DataTable([{
				text: 'ID',
				value: 'studeo_id',
				sortable: true,
			}, {
				text: 'Name',
				value: 'name',
				sortable: true,
			}, {
				text: 'Content',
				value: 'content',
				sortable: false,
			}, {
				text: 'Allow allotment adjustments?',
				value: 'allow_allotment_adjustment',
				sortable: true,
				cb: item => (item.allow_allotment_adjustment ? 'Yes' : 'No'),
			}, {
				text: 'Allow closed on arrival?',
				value: 'allow_closed_arrival',
				sortable: true,
				cb: item => (item.allow_closed_arrival ? 'Yes' : 'No'),
			}, {
				text: 'Allow closed on departure?',
				value: 'allow_closed_departure',
				sortable: true,
				cb: item => (item.allow_closed_departure ? 'Yes' : 'No'),
			}, {
				text: 'Safe allotments is enabled?',
				value: 'safe_allotments',
				sortable: true,
				cb: item => (item.safe_allotments ? 'Yes' : 'No'),
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}]),
			complexes: new DataTable([{
				text: 'ID',
				value: 'studeo_id',
				sortable: true,
			}, {
				text: 'Name',
				value: 'name',
				sortable: true,
			}, {
				text: 'Address',
				value: 'address',
				sortable: false,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}]),
			units: new DataTable([{
				text: 'ID',
				value: 'studeo_id',
				sortable: true,
			}, {
				text: 'Code',
				value: 'code',
				sortable: true,
			}, {
				text: 'Name',
				value: 'name',
				sortable: true,
			}, {
				text: 'Address',
				value: 'address',
				sortable: false,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}]),
			travelAgents: new DataTable([{
				text: 'ID',
				value: 'studeo_id',
				sortable: true,
			}, {
				text: 'Name',
				value: 'name',
				sortable: true,
			}]),
			areas: new DataTable([{
				text: 'ID',
				value: 'studeo_id',
				sortable: true,
			}, {
				text: 'Name',
				value: 'name',
				sortable: true,
			}]),
			locations: new DataTable([{
				text: 'ID',
				value: 'studeo_id',
				sortable: true,
			}, {
				text: 'Name',
				value: 'name',
				sortable: true,
			}]),
			reservations: new DataTable([{
				text: 'Folio #',
				value: 'studeo_id',
				sortable: true,
			}, {
				text: 'Unit code',
				value: 'unit_code',
				sortable: true,
			}, {
				text: 'Status',
				value: 'status',
				sortable: true,
			}, {
				text: 'Checkin',
				value: 'reservation.checkin',
				sortable: true,
				format: 'date',
			}, {
				text: 'Checkout',
				value: 'reservation.checkout',
				sortable: true,
				format: 'date',
			}, {
				text: 'Total ($)',
				value: 'amount.total',
				sortable: true,
				format: 'currency',
			}, {
				text: 'JSON',
				value: 'json',
				sortable: false,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}]),

			appliedFilters: {
				units: {},
				reservations: {},
			},
			filters: {
				units: {
					code: '',
				},
				reservations: {
					checkin: null,
					checkout: null,
					unit_code: '',
					studeo_id: '',
				},
			},
		}
	},
	computed: {
		id() {
			return this.$route.params.resort
		},
	},

	methods: {
		async fetchData(endpoint, pagination, content, filter = null) {
			const request = this.$ravello.request()
				.v(2)
				.to('resorts')
				.to(this.id)
				.to(endpoint)

			if (filter) {
				request.params(filter)
			}
			request.fromPagination(pagination)

			try {
				content.items = await request.get()
				if (content.count === -1) {
					const count = await request.count()
					content.count = count.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async fetchAreas(pagination) {
			return this.fetchData('areas', pagination, this.areas)
		},

		async fetchChannels(pagination) {
			return this.fetchData('channels', pagination, this.channels)
		},

		async fetchComplexes(pagination) {
			return this.fetchData('complexes', pagination, this.complexes)
		},

		async fetchLocations(pagination) {
			return this.fetchData('locations', pagination, this.locations)
		},

		async fetchUnits(pagination) {
			return this.fetchData('units', pagination, this.units, this.appliedFilters.units)
		},

		async fetchTravelAgents(pagination) {
			return this.fetchData('travelagents', pagination, this.travelAgents)
		},

		async fetchReservations(pagination) {
			return this.fetchData('reservations', pagination, this.reservations, this.appliedFilters.reservations)
		},

		async syncReservation(id) {
			this.$store.dispatch('app/startLoading')

			try {
				await this.$ravello.request()
					.to('system')
					.to('events')
					.to('trigger')
					.create({
						event: 'studeo.reservation.update',
						data: {
							resort: this.resort._id,
							studeoId: id,
						},
					})
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			this.$store.dispatch('app/stopLoading')
		},

		async syncChannel(studeoId) {
			this.$store.dispatch('app/startLoading')

			try {
				await this.$ravello.request()
					.to('system')
					.to('events')
					.to('trigger')
					.create({
						event: 'studeo.sync.channel',
						data: {
							resort: this.resort._id,
							studeoId,
						},
					})
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			this.$store.dispatch('app/stopLoading')
		},

		async syncComplex(studeoId) {
			this.$store.dispatch('app/startLoading')

			try {
				await this.$ravello.request()
					.to('system')
					.to('events')
					.to('trigger')
					.create({
						event: 'studeo.sync.complex',
						data: {
							resort: this.resort._id,
							studeoId,
						},
					})
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			this.$store.dispatch('app/stopLoading')
		},

		async syncUnit(code) {
			this.$store.dispatch('app/startLoading')

			try {
				await this.$ravello.request()
					.to('system')
					.to('events')
					.to('trigger')
					.create({
						event: 'studeo.sync.unit',
						data: {
							resort: this.resort._id,
							code,
						},
					})
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			this.$store.dispatch('app/stopLoading')
		},

		async getUnitsMap(unitCodes) {
			const units = await this.$ravello.request()
				.v(2)
				.to('resorts')
				.to(this.id)
				.to('units')
				.fields(['_id', 'code'])
				.params({
					code: unitCodes,
				})
				.limit(unitCodes.length)
				.get()

			return units.reduce((acc, unit) => ({ ...acc, [unit.code]: unit._id }), {})
		},

		async getChannelsMap(ids) {
			const channels = await this.$ravello.request()
				.v(2)
				.to('resorts')
				.to(this.id)
				.to('channels')
				.fields(['_id', 'studeo_id'])
				.params({
					studeo_id: ids,
				})
				.limit(ids.length)
				.get()

			return channels.reduce((acc, unit) => ({ ...acc, [unit.studeo_id]: unit._id }), {})
		},

		/*
	reservation: {
		adults: Number,
		children: Number,
		pets: Number,
		checkin: Date,
		checkout: Date,
		cancelled: Date,
		nights: Number,
	},
	amount: {
		subtotal: Number,
		taxes: Number,
		total: Number,
	},
	*/
		async onImport(data) {
			const channelIds = [...new Set(data.map(entry => entry.channel_id))]
			const unitCodes = [...new Set(data.map(entry => entry.unit_code))]

			const [unitsMap, channelsMap] = await Promise.all([
				this.getUnitsMap(unitCodes),
				this.getChannelsMap(channelIds),
			])

			const formattedData = data.map((entry) => {
				const {
					folio_number: studeoId,
					channel_id: channelId,
					unit_code: unitCode,
					folio_open_date: created,
					customer: customerName,
					arrival_date: checkin,
					departure_date: checkout,
					num_nights: nights,
					rental_revenue: total,
				} = entry

				const [last, first] = customerName.split(', ')

				return {
					studeo_id: studeoId,
					resort: this.resort._id,
					channel: channelsMap[channelId],
					unit: unitsMap[unitCode],
					status: {
						reservation: 'Imported',
						payment: 'Paid',
						is_owner_stay: false,
					},
					unit_code: unitCode,
					created,
					currency: this.resort.currency,
					imported: true,
					customer: {
						first,
						last,
					},
					reservation: {
						adults: 2,
						children: 0,
						pets: 0,
						checkin,
						checkout,
						nights,
					},
					amount: {
						subtotal: total,
						taxes: 0,
						total,
					},
					last_modified: Date.now(),
				}
			}).filter(entry => !!entry.channel && !!entry.unit)

			await this.$ravello.request()
				.v(2)
				.to('reservations')
				.to('import')
				.create(formattedData)
		},

		/* @TODO - Move to utils */
		applyFilters(type) {
			this.appliedFilters[type] = omitBy(this.filters[type], v => isNil(v) || v === '')
			this[type].count = -1
		},
	},
}
</script>
